import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Spin,
  message,
  Menu,
  Row,
  Col,
  Tag,
  Alert,
  Dropdown,
} from 'antd';
import {
  CarOutlined,
  DownloadOutlined,
  DownOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import DateRangeFilter from '../../components/DateRangeFilter';

const { Title, Text } = Typography;

const initialDateFilter = { year: dayjs().year() };

const VanReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState(initialDateFilter);
  const [vans, setVans] = useState([]);
  const [driverUtilization, setDriverUtilization] = useState([]);
  const [channelPerformance, setChannelPerformance] = useState([]);

  const buildParams = useCallback(() => {
    const params = {};
    if (dateFilter.dateRange) {
      params.startDate = dateFilter.dateRange[0]?.format('YYYY-MM-DD');
      params.endDate = dateFilter.dateRange[1]?.format('YYYY-MM-DD');
    } else if (typeof dateFilter.year !== 'undefined') {
      params.year = dateFilter.year;
      if (typeof dateFilter.month !== 'undefined') params.month = dateFilter.month;
    }
    return params;
  }, [dateFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildParams();
      const [vanRes, resourceRes] = await Promise.all([
        apiService.reports.getVanReport(params),
        apiService.insights.getResources(params),
      ]);
      setVans(vanRes.data?.data?.vans || []);
      setDriverUtilization(resourceRes.data?.driverUtilization || []);
      setChannelPerformance(resourceRes.data?.channelPerformance || []);
    } catch (err) {
      setError('Failed to load data');
      message.error('Failed to load fleet & resource data');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDateFilterChange = useCallback((newValue) => {
    setDateFilter(newValue);
  }, []);

  const handleDownload = async (format) => {
    try {
      const params = { ...buildParams(), format };
      const response = await apiService.reports.downloadVanProfitabilityReport(params);
      const mime = format === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv';
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `van_profitability_report.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      message.error('Failed to download report');
    }
  };

  const downloadMenu = (
    <Menu>
      <Menu.Item key="csv" onClick={() => handleDownload('csv')}>Download as CSV</Menu.Item>
      <Menu.Item key="excel" onClick={() => handleDownload('excel')}>Download as Excel</Menu.Item>
    </Menu>
  );

  const vanColumns = [
    {
      title: 'Registration',
      dataIndex: 'registration',
      key: 'registration',
      render: (text) => <Space><CarOutlined /><span>{text}</span></Space>,
    },
    {
      title: 'Total Jobs',
      dataIndex: 'totalJobs',
      key: 'totalJobs',
      sorter: (a, b) => a.totalJobs - b.totalJobs,
    },
    {
      title: 'Completed',
      dataIndex: 'completedJobs',
      key: 'completedJobs',
      render: (completed, record) => `${completed} / ${record.totalJobs}`,
      sorter: (a, b) => a.completedJobs - b.completedJobs,
    },
    {
      title: 'Pending',
      dataIndex: 'pendingJobs',
      key: 'pendingJobs',
      render: (pending) => (
        <Tag color={pending > 0 ? 'warning' : 'success'}>{pending}</Tag>
      ),
    },
    {
      title: 'Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      render: (v) => formatCurrency(v || 0),
      sorter: (a, b) => (a.totalRevenue || 0) - (b.totalRevenue || 0),
    },
  ];

  const driverColumns = [
    { title: 'Driver', dataIndex: 'driverName', key: 'driverName' },
    { title: 'Total Jobs', dataIndex: 'totalJobs', key: 'totalJobs', sorter: (a, b) => a.totalJobs - b.totalJobs },
    {
      title: 'Salary Earned',
      dataIndex: 'salaryEarned',
      key: 'salaryEarned',
      render: (v) => formatCurrency(v || 0),
      sorter: (a, b) => (a.salaryEarned || 0) - (b.salaryEarned || 0),
    },
  ];

  const channelColumns = [
    { title: 'Channel', dataIndex: 'channelName', key: 'channelName' },
    { title: 'Total Jobs', dataIndex: 'totalJobs', key: 'totalJobs', sorter: (a, b) => a.totalJobs - b.totalJobs },
    {
      title: 'Total Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      render: (v) => formatCurrency(v || 0),
      sorter: (a, b) => (a.totalRevenue || 0) - (b.totalRevenue || 0),
    },
    {
      title: 'Avg Job Value',
      dataIndex: 'averageJobValue',
      key: 'averageJobValue',
      render: (v) => formatCurrency(v || 0),
      sorter: (a, b) => (a.averageJobValue || 0) - (b.averageJobValue || 0),
    },
  ];

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Error" description={error} type="error" showIcon
          action={<Button type="primary" onClick={fetchData}>Retry</Button>}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2} style={{ marginBottom: 4 }}>Fleet &amp; Resources</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Van, driver, and channel performance
      </Text>

      <Card title="Filters" style={{ marginBottom: 16 }}>
        <DateRangeFilter value={dateFilter} onChange={handleDateFilterChange} />
      </Card>

      <Spin spinning={loading}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card
            title="Van Performance"
            extra={
              <Dropdown overlay={downloadMenu} placement="bottomRight">
                <Button icon={<DownloadOutlined />}>
                  Profitability Report <DownOutlined />
                </Button>
              </Dropdown>
            }
          >
            <Table
              columns={vanColumns}
              dataSource={vans}
              rowKey="_id"
              pagination={{ pageSize: 10, showTotal: (t) => `${t} vans` }}
              size="middle"
            />
          </Card>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Driver Utilization">
                <Table
                  dataSource={driverUtilization}
                  columns={driverColumns}
                  rowKey="driverName"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Channel Performance">
                <Table
                  dataSource={channelPerformance}
                  columns={channelColumns}
                  rowKey="channelName"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          </Row>
        </Space>
      </Spin>
    </div>
  );
};

export default VanReport;
